/**
 * Hash-based proof-of-work (W5-OTP-ABUSE, D383, ADR-0053) — the pure half shared by the server and the apps.
 *
 * A server-signed challenge carries a random hex `seed` and a difficulty in bits. The client searches a decimal counter
 * `n` such that SHA-256(`<seed>:<n>`) (UTF-8, here pure ASCII) starts with at least `difficultyBits` zero bits, and
 * sends `n` back as the solution. The server checks ONE hash; the client needs about 2^difficultyBits of them on average.
 * Everything here runs on Node, a browser and Hermes (React Native): no `node:crypto`, no TextEncoder, no BigInt.
 *
 * Two implementations on purpose:
 *  - {@link proofOfWorkMeets} verifies with the general {@link sha256Hex} (the one the server uses);
 *  - {@link solveProofOfWork} / {@link solveProofOfWorkAsync} search with a specialised single-block SHA-256 (the
 *    message always fits one 64-byte block, so there is no general padding, no allocation and no hex per attempt).
 * The tests pin that the fast search and the general verifier (and `node:crypto`) agree.
 *
 * Neutral: bytes, bits and counters only.
 */
import { sha256Hex } from './hash.js';
/** The algorithm name carried on the wire. */
export const PROOF_OF_WORK_ALGORITHM = 'sha256-leading-zero-bits';
/** Difficulty bounds: below 1 is no work, above 32 is beyond a phone in any reasonable time (and one word of hash). */
export const PROOF_OF_WORK_MIN_BITS = 1;
export const PROOF_OF_WORK_MAX_BITS = 32;
/** A seed is lowercase hex, 16..38 characters, so `<seed>:<counter>` always fits one SHA-256 block (≤ 55 bytes). */
const SEED = /^[0-9a-f]{16,38}$/;
/** A solution is a decimal counter without leading zeros, at most 16 digits (fits the block, exact in a double). */
const SOLUTION = /^(0|[1-9][0-9]{0,15})$/;
export function isProofOfWorkSeed(seed) {
    return typeof seed === 'string' && SEED.test(seed);
}
export function isProofOfWorkSolution(solution) {
    return typeof solution === 'string' && SOLUTION.test(solution) && Number.isSafeInteger(Number(solution));
}
const validBits = (bits) => Number.isInteger(bits) && bits >= PROOF_OF_WORK_MIN_BITS && bits <= PROOF_OF_WORK_MAX_BITS;
/** The number of leading zero bits of a lowercase-hex digest (only the first 32 bits matter here). */
export function leadingZeroBitsOfHex(hex) {
    const word = Number.parseInt(hex.slice(0, 8), 16);
    return Number.isNaN(word) ? 0 : Math.clz32(word);
}
/** True when `solution` solves the challenge `seed` at `difficultyBits` (the general verifier: one {@link sha256Hex}). */
export function proofOfWorkMeets(seed, solution, difficultyBits) {
    if (!isProofOfWorkSeed(seed) || !isProofOfWorkSolution(solution) || !validBits(difficultyBits))
        return false;
    return leadingZeroBitsOfHex(sha256Hex(`${seed}:${solution}`)) >= difficultyBits;
}
// ---- the fast search: SHA-256 over exactly one block ---------------------------------------------------------------
const K = new Int32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);
function contextFor(seed) {
    const text = `${seed}:`;
    const prefix = new Uint8Array(text.length);
    for (let i = 0; i < text.length; i += 1)
        prefix[i] = text.charCodeAt(i);
    return { prefix, block: new Uint8Array(64), w: new Int32Array(64) };
}
/** The first word of SHA-256(`<seed>:<counter>`) — the only word a difficulty ≤ 32 looks at. */
function firstWord(ctx, counter) {
    const { prefix, block, w } = ctx;
    block.fill(0);
    block.set(prefix);
    const digits = String(counter);
    let len = prefix.length;
    for (let i = 0; i < digits.length; i += 1)
        block[len++] = digits.charCodeAt(i);
    block[len] = 0x80;
    const bitLen = len * 8;
    block[62] = (bitLen >>> 8) & 0xff;
    block[63] = bitLen & 0xff;
    for (let i = 0; i < 16; i += 1) {
        const o = i * 4;
        w[i] = (block[o] << 24) | (block[o + 1] << 16) | (block[o + 2] << 8) | block[o + 3];
    }
    for (let i = 16; i < 64; i += 1) {
        const x = w[i - 15];
        const y = w[i - 2];
        const s0 = ((x >>> 7) | (x << 25)) ^ ((x >>> 18) | (x << 14)) ^ (x >>> 3);
        const s1 = ((y >>> 17) | (y << 15)) ^ ((y >>> 19) | (y << 13)) ^ (y >>> 10);
        w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
    }
    let a = 0x6a09e667 | 0;
    let b = 0xbb67ae85 | 0;
    let c = 0x3c6ef372 | 0;
    let d = 0xa54ff53a | 0;
    let e = 0x510e527f | 0;
    let f = 0x9b05688c | 0;
    let g = 0x1f83d9ab | 0;
    let h = 0x5be0cd19 | 0;
    for (let i = 0; i < 64; i += 1) {
        const S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
        const ch = (e & f) ^ (~e & g);
        const t1 = (h + S1 + ch + K[i] + w[i]) | 0;
        const S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
        const maj = (a & b) ^ (a & c) ^ (b & c);
        const t2 = (S0 + maj) | 0;
        h = g;
        g = f;
        f = e;
        e = (d + t1) | 0;
        d = c;
        c = b;
        b = a;
        a = (t1 + t2) | 0;
    }
    return (0x6a09e667 + a) | 0;
}
/**
 * Search `count` counters from `start` for a solution (synchronous; one slice of work). Throws a RangeError on a seed
 * or difficulty outside the contract, so a malformed challenge is never searched forever.
 */
export function solveProofOfWork(seed, difficultyBits, start = 0, count = Number.MAX_SAFE_INTEGER) {
    if (!isProofOfWorkSeed(seed))
        throw new RangeError('a proof-of-work seed is 16..38 lowercase hex characters');
    if (!validBits(difficultyBits))
        throw new RangeError(`difficulty must be an integer in [${PROOF_OF_WORK_MIN_BITS}, ${PROOF_OF_WORK_MAX_BITS}]`);
    const ctx = contextFor(seed);
    const end = Math.min(Number.MAX_SAFE_INTEGER, start + count);
    for (let n = start; n < end; n += 1) {
        if (Math.clz32(firstWord(ctx, n)) >= difficultyBits)
            return { solution: String(n), next: n + 1 };
    }
    return { next: end };
}
const defaultYield = () => new Promise((resolve) => setTimeout(resolve, 0));
/**
 * Search in slices, yielding to the host between them, reporting progress. Resolves to the solution, or `undefined` when
 * stopped or when `maxTries` ran out.
 */
export async function solveProofOfWorkAsync(seed, difficultyBits, options = {}) {
    const slice = Math.max(1, Math.floor(options.sliceSize ?? 4096));
    const expected = 2 ** difficultyBits;
    const maxTries = options.maxTries ?? expected * 64;
    const yieldToHost = options.yieldToHost ?? defaultYield;
    let next = 0;
    while (next < maxTries) {
        if (options.shouldStop?.() === true)
            return undefined;
        const found = solveProofOfWork(seed, difficultyBits, next, Math.min(slice, maxTries - next));
        if (found.solution !== undefined) {
            options.onProgress?.({ tried: found.next, expected });
            return found.solution;
        }
        next = found.next;
        options.onProgress?.({ tried: next, expected });
        await yieldToHost();
    }
    return undefined;
}
//# sourceMappingURL=proof-of-work.js.map